/**
 * Inbox data hooks — react-query, org-scoped.
 *
 * relay.inbox_conversations and relay.inbox_messages are not in the
 * generated Database types yet. We use the `as never` cast pattern —
 * the same approach used by social_engagements in features/social/api.ts
 * and by relayFrom() in features/contacts/api.ts — to access relay-schema
 * tables that the type generator hasn't picked up yet.
 *
 * Refresh strategy: polling at staleTime=15s (matches useSmsConversations).
 * SmsInbox uses polling (no realtime channel in api.ts), so we mirror that.
 * The inbox tables were added to the supabase_realtime publication, but
 * the SMS feature doesn't use realtime — we stay consistent and poll.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { InboxConversation, InboxMessage, InboxStatus } from "./types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Access a relay-schema table that isn't in the generated Database types.
 * Mirrors the `relayFrom()` helper in features/contacts/api.ts and the
 * `.from("social_engagements" as never)` pattern in features/social/api.ts.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function inboxFrom(table: string): any {
  return supabase.from(table as never);
}

// ---------------------------------------------------------------------------
// Conversation list
// ---------------------------------------------------------------------------

/**
 * All non-closed conversations for the org, newest-first.
 * Joins the linked contact to derive a display name.
 */
export function useInboxConversations(organizationId: string | null) {
  return useQuery({
    queryKey: ["inbox-conversations", organizationId],
    enabled: !!organizationId,
    staleTime: 15_000,
    queryFn: async (): Promise<InboxConversation[]> => {
      if (!organizationId) return [];

      // Fetch conversations with a contact join in one query.
      // The `as never` cast on table name + any-typed return handles the
      // missing generated types for relay.inbox_conversations.
      const { data, error } = await inboxFrom("inbox_conversations")
        .select(`
          id, organization_id, contact_id,
          last_message_at, last_message_preview,
          last_message_channel, last_message_direction,
          unread_count, last_read_at, assigned_to,
          status, snoozed_until, created_at, updated_at,
          contact:contacts(id, first_name, last_name, email, phone, mobile, sms_consent)
        `)
        .eq("organization_id", organizationId)
        .neq("status", "closed")
        .order("last_message_at", { ascending: false, nullsFirst: false })
        .limit(500);

      if (error) throw error;

      return ((data as unknown[]) ?? []).map((row: unknown): InboxConversation => {
        const r = row as Record<string, unknown>;
        const c = r.contact as {
          first_name: string | null;
          last_name: string | null;
          email: string | null;
          phone: string | null;
          mobile: string | null;
          sms_consent: boolean | null;
        } | null;
        const firstName = c?.first_name ?? null;
        const lastName = c?.last_name ?? null;
        const nameRaw = [firstName, lastName].filter(Boolean).join(" ").trim();
        return {
          id: r.id as string,
          organization_id: r.organization_id as string,
          contact_id: (r.contact_id as string | null) ?? null,
          contact_name: nameRaw || null,
          contact_email: c?.email ?? null,
          contact_phone: c?.phone ?? c?.mobile ?? null,
          contact_sms_consent: c?.sms_consent ?? null,
          last_message_at: (r.last_message_at as string | null) ?? null,
          last_message_preview: (r.last_message_preview as string | null) ?? null,
          last_message_channel: (r.last_message_channel as InboxConversation["last_message_channel"]) ?? null,
          last_message_direction: (r.last_message_direction as InboxConversation["last_message_direction"]) ?? null,
          unread_count: (r.unread_count as number) ?? 0,
          last_read_at: (r.last_read_at as string | null) ?? null,
          assigned_to: (r.assigned_to as string | null) ?? null,
          status: (r.status as InboxConversation["status"]) ?? "open",
          snoozed_until: (r.snoozed_until as string | null) ?? null,
          created_at: r.created_at as string,
          updated_at: r.updated_at as string,
        };
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Unread total (for nav badge)
// ---------------------------------------------------------------------------

/**
 * Sum of unread_count across open conversations — used for the nav dot badge.
 * Derives from the conversations query cache rather than a separate DB call
 * when possible; but this standalone hook lets AppShell subscribe independently.
 */
export function useInboxUnreadTotal(organizationId: string | null) {
  return useQuery({
    queryKey: ["inbox-unread-total", organizationId],
    enabled: !!organizationId,
    staleTime: 15_000,
    queryFn: async (): Promise<number> => {
      if (!organizationId) return 0;
      const { data, error } = await inboxFrom("inbox_conversations")
        .select("unread_count")
        .eq("organization_id", organizationId)
        .eq("status", "open")
        .gt("unread_count", 0)
        .limit(500);
      if (error) throw error;
      const rows = (data as unknown[]) ?? [];
      return rows.reduce((sum: number, row: unknown) => {
        const r = row as Record<string, unknown>;
        return sum + ((r.unread_count as number) ?? 0);
      }, 0 as number);
    },
  });
}

// ---------------------------------------------------------------------------
// Messages for a conversation
// ---------------------------------------------------------------------------

export function useInboxMessages(
  conversationId: string | null,
  organizationId: string | null,
) {
  return useQuery({
    queryKey: ["inbox-messages", organizationId, conversationId],
    enabled: !!conversationId && !!organizationId,
    staleTime: 10_000,
    queryFn: async (): Promise<InboxMessage[]> => {
      if (!conversationId || !organizationId) return [];
      const { data, error } = await inboxFrom("inbox_messages")
        .select(
          "id, organization_id, conversation_id, contact_id, channel, direction, sender_user_id, subject, body, external_id, status, metadata, occurred_at",
        )
        .eq("organization_id", organizationId)
        .eq("conversation_id", conversationId)
        .order("occurred_at", { ascending: true })
        .limit(500);
      if (error) throw error;
      return ((data as unknown[]) ?? []).map((row: unknown): InboxMessage => {
        const r = row as Record<string, unknown>;
        return {
          id: r.id as string,
          organization_id: r.organization_id as string,
          conversation_id: r.conversation_id as string,
          contact_id: (r.contact_id as string | null) ?? null,
          channel: r.channel as InboxMessage["channel"],
          direction: r.direction as InboxMessage["direction"],
          sender_user_id: (r.sender_user_id as string | null) ?? null,
          subject: (r.subject as string | null) ?? null,
          body: (r.body as string | null) ?? null,
          external_id: (r.external_id as string | null) ?? null,
          status: (r.status as string | null) ?? null,
          metadata: (r.metadata as Record<string, unknown> | null) ?? null,
          occurred_at: r.occurred_at as string,
        };
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Mark conversation read
// ---------------------------------------------------------------------------

export function useMarkConversationRead(organizationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      if (!organizationId) throw new Error("No active organization");
      const { error } = await inboxFrom("inbox_conversations")
        .update({ unread_count: 0, last_read_at: new Date().toISOString() })
        .eq("id", conversationId)
        .eq("organization_id", organizationId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inbox-conversations", organizationId] });
      qc.invalidateQueries({ queryKey: ["inbox-unread-total", organizationId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Set conversation status
// ---------------------------------------------------------------------------

export function useSetConversationStatus(organizationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId,
      status,
    }: {
      conversationId: string;
      status: InboxStatus;
    }) => {
      if (!organizationId) throw new Error("No active organization");
      const { error } = await inboxFrom("inbox_conversations")
        .update({ status })
        .eq("id", conversationId)
        .eq("organization_id", organizationId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inbox-conversations", organizationId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Send reply via Edge Function
// ---------------------------------------------------------------------------

export function useSendReply(organizationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      conversation_id: string;
      channel: "sms" | "email";
      body: string;
      subject?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke("relay-inbox-reply", {
        body: payload,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate messages + conversations so the UI refreshes via polling.
      qc.invalidateQueries({
        queryKey: ["inbox-messages", organizationId, variables.conversation_id],
      });
      qc.invalidateQueries({ queryKey: ["inbox-conversations", organizationId] });
      qc.invalidateQueries({ queryKey: ["inbox-unread-total", organizationId] });
    },
  });
}
